import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";

const AuthContext = createContext(null);
const LOCAL_STORAGE_KEY = "carefleet_auth_token";
const SESSION_STORAGE_KEY = "carefleet_auth_token_session";
const STORAGE_MODE_KEY = "carefleet_auth_storage_mode";

const getStoredToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY) || null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);

  const clearStoredToken = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(STORAGE_MODE_KEY);
    delete axios.defaults.headers.common.Authorization;
  }, []);

  const setAuthToken = useCallback((nextToken, rememberMe = true) => {
    clearStoredToken();

    if (nextToken) {
      const storage = rememberMe ? localStorage : sessionStorage;
      const storageKey = rememberMe ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY;
      storage.setItem(storageKey, nextToken);
      localStorage.setItem(STORAGE_MODE_KEY, rememberMe ? "local" : "session");
      axios.defaults.headers.common.Authorization = `Bearer ${nextToken}`;
    }

    setToken(nextToken);
  }, [clearStoredToken]);

  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      try {
        const response = await axios.get(`${API_BASE}/auth/me`);
        setUser(response.data);
      } catch (error) {
        clearStoredToken();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [token, clearStoredToken]);

  const login = async (email, password, options = {}) => {
    const rememberMe = options.rememberMe ?? true;
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
      setAuthToken(response.data.access_token, rememberMe);
      setUser(response.data.user);
      return { success: true, data: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Unable to sign in",
      };
    }
  };

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    setUser(null);
  }, [clearStoredToken]);

  const requestPasswordReset = async (email) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/password-reset/request`, { email });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Unable to request reset",
      };
    }
  };

  const validateResetToken = async (resetToken) => {
    try {
      const response = await axios.get(`${API_BASE}/auth/password-reset/validate?token=${encodeURIComponent(resetToken)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Reset token is invalid",
      };
    }
  };

  const confirmReset = async (resetToken, newPassword) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/password-reset/confirm`, {
        token: resetToken,
        new_password: newPassword,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Unable to reset password",
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(response.data.user);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Unable to update password",
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: Boolean(user),
    login,
    logout,
    requestPasswordReset,
    validateResetToken,
    confirmReset,
    changePassword,
    refreshCurrentUser: async () => {
      const response = await axios.get(`${API_BASE}/auth/me`);
      setUser(response.data);
      return response.data;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
